/****************************************************
 * Module Name:  showLabStatsModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Constants from "../../utility/constants";
import { Box, Button, CircularProgress, FormControlLabel, Switch, Tooltip, Typography } from "@mui/material";
import logo from '../../images/pansuriya.jpg';
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CommonSnackbar from "../common/commonSnackBar";
import Utility from "../../utility/utility";
import renderCellExpand from "../common/dataGridCellExpand";
import { styled } from "@mui/material/styles";

const PrioritySwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
                opacity: 1,
                border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: '#33cf4d',
            border: '6px solid #fff',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color:
                theme.palette.mode === 'light'
                    ? theme.palette.grey[100]
                    : theme.palette.grey[600],
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));

const ShowLabStatsModal = ({
    show,
    setShow,
    handleStats,
    labStats,
    setLabStats,
    loading,
    setLoadingButton,
    labName,
    showStatsError,
    setShowStatsError
}) => {

    const [showPriorityWise, setShowPriorityWise] = useState(false);
    const [resultData, setResultData] = useState([]);

    useEffect(() => {
        handleStats();
    }, []);

    useEffect(() => {
        if (labStats.length > 0)
            handlePriorityWise();

    }, [labStats]);

    useEffect(() => {
        if (labStats.length > 0)
            handlePriorityWise();
    }, [showPriorityWise]);

    const handlePriorityWise = () => {
        setLoadingButton(true);
        if (showPriorityWise) {
            setResultData(labStats);
            setLoadingButton(false);
        } else {
            const statusWiseMap = new Map();

            labStats.forEach(element => {
                if (statusWiseMap.has(element.status)) {
                    statusWiseMap.set(element.status, statusWiseMap.get(element.status) + element.count)
                } else {
                    statusWiseMap.set(element.status, element.count)

                }
            });
            const result = Array.from(statusWiseMap, ([status, count]) => ({ status, count }));
            setResultData(result);
            setLoadingButton(false);
        }
    }

    const handleClose = () => {
        setLabStats([]);
        setShow(false);
    }
    const closeErrorDialog = () => setShowStatsError(false);

    const columns = [
        {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: (params) => {
                if (params.row.id === rows.length) {
                    return (
                        <Box style={{ fontWeight: 'bold' }}>
                            {params.value}
                        </Box>
                    );
                }
            }
        },
        {
            field: "priority",
            headerName: "PRIORITY",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand
        },
        {
            field: "count",
            headerName: "COUNT",
            headerAlign: "center",
            align: "center",
            minWidth: 130,
            renderCell: (params) => {
                if (params.row.id === rows.length) {
                    return (
                        <Box style={{ fontWeight: 'bold' }}>
                            {Utility.NumberToLocaleString(params.value)}
                        </Box>
                    );
                }
                return Utility.NumberToLocaleString(params.value);
            }
        }
    ];

    const rows = resultData.map((row, index) => ({ ...row, id: index + 1 }));

    const columnNames = labName !== "IGI" || !showPriorityWise ? columns.filter(col => col.field !== "priority") : [...columns];

    return (
        <div>
            <CommonSnackbar
                show={showStatsError}
                message={
                    <Box>
                        Lab Stats Response Fetch Failed
                        <br /> Please try again later.
                    </Box>
                }
                variant={"error"}
                autoHide={false}
                handleClose={closeErrorDialog}
                title="Response Error"
            />
            <Modal
                show={show}
                onHide={handleClose}
                backdrop="static"
                keyboard={true}
                size="lg"
                enforceFocus={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;{labName} Stats
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Box px={1} pt={1} sx={{
                        position: "relative",
                    }}>

                        <Box sx={{ height: 400, width: '100%' }}>
                            <DataGridPro
                                loading={loading}
                                rows={rows}
                                columns={columnNames}
                                disableRowSelectionOnClick={true}
                                density="compact"
                                initialState={{
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slots={{ toolbar: GridToolbar }}
                                slotProps={{
                                    toolbar: () => null,
                                }}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        fontSize: '16px',
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                            />
                        </Box>
                        <Box sx={{
                            position: "absolute",
                            top: labName === "IGI" ? "0.05rem" : "0.8rem",
                            right: "1rem",
                            display: "flex",
                            alignItems: "center",
                        }}>
                            {labName === "IGI" ?
                                <Box display={"flex"} alignItems={"center"}
                                    sx={{
                                        "& .MuiFormControlLabel-root": {
                                            margin: 0
                                        },
                                        "& .MuiSwitch-root": {
                                            width: "38px",
                                            height: "22px"
                                        },
                                        "& .MuiSwitch-thumb": {
                                            width: "18px !important",
                                            height: "18px !important"
                                        },
                                        margin: "8px"

                                    }}
                                >
                                    <FormControlLabel
                                        control={<PrioritySwitch sx={{ m: 1 }} />}
                                        onChange={() => setShowPriorityWise(!showPriorityWise)}
                                        checked={showPriorityWise}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: "15px",
                                        }}
                                    > Priority Wise</Typography>
                                </Box>
                                : null}

                            {loading ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() =>
                                            handleStats()
                                        }
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer", marginRight: "5px" }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="error" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ShowLabStatsModal;
